import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Search, CheckCircle2, CreditCard, ShoppingCart, BarChart3, Users, Mail, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { RecipeCard } from '@/components/recipes/RecipeCard';
import { SEO, generateWebsiteSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { useFeaturedRecipes } from '@/hooks/useRecipes';

const TOOL_CATEGORIES = [
  { name: 'Payments', icon: CreditCard, slug: 'payments', tools: ['Stripe', 'PayPal'], exampleRecipe: { title: 'Stripe → QuickBooks', slug: 'stripe-quickbooks-integration' } },
  { name: 'E-commerce', icon: ShoppingCart, slug: 'ecommerce', tools: ['Shopify', 'Amazon'], exampleRecipe: { title: 'Shopify → QuickBooks', slug: 'shopify-quickbooks-integration' } },
  { name: 'Accounting', icon: Calculator, slug: 'accounting', tools: ['QuickBooks', 'Xero'], exampleRecipe: { title: 'Xero → Sheets', slug: 'xero-google-sheets-integration' } },
  { name: 'CRM', icon: Users, slug: 'crm', tools: ['HubSpot', 'Salesforce'], exampleRecipe: { title: 'Shopify → HubSpot', slug: 'shopify-hubspot-integration' } },
  { name: 'Marketing', icon: Mail, slug: 'marketing', tools: ['Mailchimp', 'Klaviyo'], exampleRecipe: { title: 'Mailchimp Sync', slug: 'mailchimp-integration' } },
  { name: 'Analytics', icon: BarChart3, slug: 'analytics', tools: ['Google Analytics'], exampleRecipe: { title: 'Analytics Setup', slug: 'google-analytics-integration' } },
];

export default function Index() {
  const { data: featuredRecipes, isLoading } = useFeaturedRecipes();

  const structuredData = [
    generateWebsiteSchema(),
    generateBreadcrumbSchema([{ name: 'Home', url: '/' }]),
  ];

  return (
    <PublicLayout>
      <SEO
        canonical="/"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(217_91%_60%/0.15),transparent_50%)]" />
        <div className="container mx-auto px-4 py-20 lg:py-28 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur mb-6">
              <Zap className="h-4 w-4 text-accent" />
              <span className="text-sm">Automation made simple</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Don't Replace It.{' '}
              <span className="text-gradient">Automate It.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Proven automation recipes to connect your tools.
              Step-by-step guides for Make, n8n, and Zapier.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/recipes">
                <Button 
                  size="lg" 
                  className="bg-white text-foreground hover:bg-accent hover:text-accent-foreground shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 gap-2"
                >
                  <Search className="h-5 w-5" />
                  Browse All Recipes
                </Button>
              </Link>
              <Link to="/tools">
                <Button 
                  size="lg" 
                  className="bg-white/10 border-2 border-white/50 text-white hover:bg-white hover:text-foreground hover:border-white transition-all duration-200 gap-2"
                >
                  Browse Tools Directory
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tool Categories - ABOVE THE FOLD */}
      <section className="py-12 bg-background border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold">Find Recipes by Category</h2>
            <Link to="/tools">
              <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-primary">
                All Tools <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {TOOL_CATEGORIES.map((category) => (
              <div 
                key={category.slug} 
                className="group flex flex-col items-center p-4 rounded-xl border bg-card hover:border-primary hover:shadow-md transition-all duration-200"
              >
                <Link 
                  to={`/recipes?category=${category.slug}`}
                  className="flex flex-col items-center"
                >
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <category.icon className="h-5 w-5" />
                  </div>
                  <span className="font-medium text-sm text-center">{category.name}</span>
                  <span className="text-xs text-muted-foreground mt-1">{category.tools.join(', ')}</span>
                </Link>
                {/* Direct recipe link for SEO/bot crawlability */}
                <Link 
                  to={`/recipes/${category.exampleRecipe.slug}`}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  {category.exampleRecipe.title} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Recipes */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold">Latest Automation Recipes</h2>
            <Link to="/recipes">
              <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-primary">
                View All <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          ) : featuredRecipes && featuredRecipes.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredRecipes.slice(0, 6).map((recipe) => (
                <RecipeCard key={recipe.id} recipe={recipe} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-muted/30 rounded-xl">
              <p className="text-muted-foreground mb-4">Explore our automation library</p>
              <Link to="/recipes">
                <Button variant="outline" className="gap-2">
                  <Search className="h-4 w-4" />
                  Browse All Recipes
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Features - Moved Below */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-8">Why Use Our Recipes?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: CheckCircle2, title: 'Verified & Tested', desc: 'Every recipe is tested with real tools and verified to work.' },
              { icon: Zap, title: 'Step-by-Step', desc: 'Clear instructions with data mapping and troubleshooting tips.' },
              { icon: Search, title: 'Find Fast', desc: 'Search by tool, category, or use case to find what you need.' },
            ].map((feature, i) => (
              <div key={i} className="text-center p-6 bg-background rounded-xl border">
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 text-primary mb-4">
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
